/**
 * Shared publish-job model. Owned by App state so it survives navigation:
 * Home produces status updates while the Publish page consumes them. This is
 * what makes the Publish loader truth-driven instead of timer-driven.
 */

export type PublishJobStatus = 'idle' | 'working' | 'ready' | 'error';

export type PublishJobStage =
  | 'sending'
  | 'stitching'
  | 'downloading'
  | 'recording';

export interface PublishJob {
  status: PublishJobStatus;
  /** Current phase; null when idle. */
  stage: PublishJobStage | null;
  /** Failure message; set only when status is 'error'. */
  error: string | null;
  /** Distinguishes superseded jobs from the current one. */
  jobId: string;
}

export const IDLE_PUBLISH_JOB: PublishJob = {
  status: 'idle',
  stage: null,
  error: null,
  jobId: '',
};

export const newPublishJobId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

/**
 * Honest staged text (no fake percentages). Stays generic on purpose: the
 * record-fallback stage must never reveal backdrop recording to the user.
 */
export const PUBLISH_JOB_STAGE_LABELS: Record<PublishJobStage, string> = {
  sending: 'Sending scenes…',
  stitching: 'Stitching on server…',
  downloading: 'Downloading video…',
  recording: 'Preparing your video…',
};
